import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { sanitizeEmail } from "@/lib/validation-utils";
import {
	FetchMenteesResponse,
	FetchMenteesMentee,
} from "@/types/supabase-functions";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Badge,
} from "@/components/primitives";
import { Search, Eye, UserPlus, Trash2 } from "lucide-react";

interface Mentee {
	id: string;
	name: string;
	email: string;
	status?: string;
	reflections_count?: number;
	last_activity?: string;
	invited_at?: string;
	created_at?: string;
	groups?: string[];
}

export default function MyMentees() {
	const navigate = useNavigate();
	const [mentees, setMentees] = useState<Mentee[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [groupFilter, setGroupFilter] = useState<string>("all");
	const [inviteEmail, setInviteEmail] = useState("");

	useEffect(() => {
		fetchMentees();
	}, []);

	const fetchMentees = async () => {
		try {
			setLoading(true);
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session?.access_token) return;

			const { data, error } =
				await supabase.functions.invoke<FetchMenteesResponse>("fetch-mentees", {
					headers: { Authorization: `Bearer ${session.access_token}` },
				});

			if (error) {
				console.error("Error fetching mentees edge function:", error);
				setMentees([]);
				setLoading(false);
				return;
			}

			const menteesData = (data.mentees || []).map((m: FetchMenteesMentee) => ({
				id: m.id,
				name: m.name,
				email: m.email,
				status: m.status || "accepted",
				reflections_count: m.reflections_count || 0,
				last_activity: m.last_activity || m.created_at,
				invited_at: m.invited_at,
			}));

			// Batch fetch all group memberships to avoid N+1 query pattern
			const menteeIds = menteesData.map((m) => m.id);
			const { data: allMemberships } = await supabase
				.from("group_memberships")
				.select("mentee_id, groups!inner(name)")
				.in("mentee_id", menteeIds);

			// Create a map of mentee_id -> groups[]
			const groupsByMentee = new Map<string, string[]>();
			allMemberships?.forEach((membership: any) => {
				const menteeId = membership.mentee_id;
				const groupName = membership.groups?.name;
				if (menteeId && groupName) {
					if (!groupsByMentee.has(menteeId)) {
						groupsByMentee.set(menteeId, []);
					}
					groupsByMentee.get(menteeId)!.push(groupName);
				}
			});

			// Map mentees with their groups
			const menteesWithGroups = menteesData.map((mentee: any) => ({
				...mentee,
				groups: groupsByMentee.get(mentee.id) || [],
			}));

			setMentees(menteesWithGroups);
		} catch (err) {
			console.error(err);
			setMentees([]);
		} finally {
			setLoading(false);
		}
	};

	const getUniqueGroups = () => {
		const allGroups = mentees.flatMap((m) => m.groups || []);
		return [...new Set(allGroups)].sort();
	};

	const inviteMentee = async () => {
		if (!inviteEmail.trim()) return;

		// Validate email format
		const sanitizedEmail = sanitizeEmail(inviteEmail.trim());
		if (!sanitizedEmail) {
			alert("Please enter a valid email address");
			return;
		}

		try {
			// Use secure edge function instead of direct database access
			const { data, error } = await supabase.functions.invoke("invite-mentee", {
				body: { email: sanitizedEmail },
			});

			if (error) {
				console.error("Edge function error:", error);
				alert("Error sending invitation. Please try again.");
				return;
			}

			if (data?.error) {
				// Handle specific error cases from edge function
				if (data.error === "User not found") {
					alert(
						data.details ||
							"No user found with that email address. They need to create an account first.",
					);
				} else if (data.error === "Relationship exists") {
					alert(
						data.details ||
							"This user is already your mentee or has a pending invitation.",
					);
				} else {
					alert("Error sending invitation: " + (data.details || data.error));
				}
				setInviteEmail("");
				return;
			}

			if (data?.success) {
				alert(data.message || "Invitation sent successfully");
				setInviteEmail("");
				fetchMentees();
			}
		} catch (err) {
			console.error("Error inviting mentee:", err);
			alert("Error sending invitation. Please try again.");
		}
	};

	const removeMentee = async (menteeId: string) => {
		const confirmed = confirm("Remove this mentee?");
		if (!confirmed) return;
		try {
			const { error } = await supabase
				.from("mentor_mentee_relationships")
				.delete()
				.eq("mentee_id", menteeId);
			if (error) {
				alert("Error removing mentee: " + error.message);
			} else {
				fetchMentees();
			}
		} catch (err) {
			console.error(err);
		}
	};

	const filtered = mentees.filter((mentee) => {
		const matchesSearch = `${mentee.name} ${mentee.email}`
			.toLowerCase()
			.includes(searchTerm.toLowerCase());
		const matchesStatus =
			statusFilter === "all" || mentee.status === statusFilter;
		const matchesGroup =
			groupFilter === "all" ||
			(mentee.groups || []).some((g) =>
				g.toLowerCase().includes(groupFilter.toLowerCase()),
			);
		return matchesSearch && matchesStatus && matchesGroup;
	});

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl">
			<h1 className="text-2xl font-bold mb-4">My Mentees</h1>
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>Assign new mentee</CardTitle>
						<CardDescription>
							Send an invitation to add a new mentee to your dashboard
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex gap-2">
							<Input
								placeholder="Enter email to invite"
								value={inviteEmail}
								onChange={(e) => setInviteEmail(e.target.value)}
								className="flex-1"
							/>
							<Button onClick={inviteMentee}>
								<UserPlus className="w-4 h-4 mr-2" />
								Invite
							</Button>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>All my mentees</CardTitle>
						<CardDescription>
							Manage your mentee relationships and invitations
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="mb-4 flex flex-col sm:flex-row gap-4">
							<div className="flex-1">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
									<Input
										placeholder="Search by name or email..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10"
									/>
								</div>
							</div>
							<div className="flex gap-2">
								<Select value={statusFilter} onValueChange={setStatusFilter}>
									<SelectTrigger className="w-40">
										<SelectValue placeholder="Filter by status" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Status</SelectItem>
										<SelectItem value="accepted">Accepted</SelectItem>
										<SelectItem value="pending">Pending</SelectItem>
										<SelectItem value="declined">Declined</SelectItem>
									</SelectContent>
								</Select>
								<Select value={groupFilter} onValueChange={setGroupFilter}>
									<SelectTrigger className="w-40">
										<SelectValue placeholder="Filter by group" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Groups</SelectItem>
										{getUniqueGroups().map((g) => (
											<SelectItem key={g} value={g}>
												{g}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						{loading ? (
							<div className="text-center py-8">Loading...</div>
						) : (
							<div className="rounded-md border overflow-auto">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-[200px]">Name</TableHead>
											<TableHead className="w-[250px]">Email</TableHead>
											<TableHead className="w-[200px]">Groups</TableHead>
											<TableHead className="w-[120px]">Status</TableHead>
											<TableHead className="w-[150px]">Last Activity</TableHead>
											<TableHead className="w-[100px]">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filtered.map((mentee) => (
											<TableRow key={mentee.id} className="hover:bg-muted/50">
												<TableCell className="font-medium">
													{mentee.name}
												</TableCell>
												<TableCell>{mentee.email}</TableCell>
												<TableCell>
													{mentee.groups && mentee.groups.length > 0 ? (
														<div className="flex flex-wrap gap-1">
															{mentee.groups.map((g) => (
																<Badge
																	key={g}
																	variant="outline"
																	className="text-xs"
																>
																	{g}
																</Badge>
															))}
														</div>
													) : (
														<span className="text-xs text-muted-foreground">
															-
														</span>
													)}
												</TableCell>
												<TableCell>
													<Badge className="text-xs bg-green-100 text-green-800">
														{mentee.status}
													</Badge>
												</TableCell>
												<TableCell>
													<span className="text-xs text-muted-foreground">
														{mentee.last_activity
															? new Date(
																	mentee.last_activity,
																).toLocaleDateString()
															: "-"}
													</span>
												</TableCell>
												<TableCell className="flex items-center justify-end space-x-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => navigate(`/mentee/${mentee.id}`)}
														className="text-primary hover:text-primary"
													>
														<Eye className="w-4 h-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => removeMentee(mentee.id)}
														className="text-destructive hover:text-destructive"
														aria-label={`Remove ${mentee.name}`}
														title="Remove mentee"
													>
														<Trash2 className="w-4 h-4" />
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
